import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Badge, Group, Stack, Text, Title } from '@mantine/core';
import { IconBuilding } from '@tabler/icons-react';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { QuickCreateButton } from '@/shared/components/QuickCreateButton';
import type { Tenant } from '@/shared/types/entities';

export const Route = createFileRoute('/_app/admin/tenants/')({ component: TenantsListPage });

function TenantsListPage() {
  const navigate = useNavigate();
  return (
    <Stack>
      <Group>
        <IconBuilding size={28} />
        <Title order={1}>Tenant (aziende)</Title>
      </Group>
      <Text c="dimmed" size="sm">
        Solo SuperAdmin può vedere e gestire i tenant del sistema. Per il branding del tenant corrente usa <code>/admin/branding</code>.
      </Text>
      <ResourceListPage<Tenant>
        title=""
        type="tenants"
        initialPageSize={50}
        columns={[
          { accessorKey: 'name', header: 'Nome' },
          { accessorKey: 'organization', header: 'Organizzazione' },
          { accessorKey: 'plan', header: 'Piano', size: 110, Cell: ({ cell }) => <Badge variant="light">{cell.getValue<string>() ?? 'free'}</Badge> },
          { accessorKey: 'enabled', header: 'Attivo', size: 90, Cell: ({ cell }) => cell.getValue<boolean>() ? <Badge color="green">Sì</Badge> : <Badge color="red">No</Badge> },
          { accessorKey: 'email', header: 'Email' },
          { accessorKey: 'vatNumber', header: 'P.IVA', size: 140 },
          { accessorKey: 'country', header: 'Paese', size: 80 },
        ]}
        searchField="name"
        onRowClick={(t) => void navigate({ to: `/admin/tenants/${t.id}` })}
        emptyMessage="Nessun tenant"
        emptyDescription="Crea il primo tenant con il bottone «Nuovo tenant» nella toolbar."
        emptyIcon={IconBuilding}
        toolbarRight={
          <QuickCreateButton
            type="tenants"
            title="Nuovo tenant"
            fields={[
              { name: 'name', label: 'Nome', required: true },
              { name: 'organization', label: 'Organizzazione' },
              { name: 'email', label: 'Email', placeholder: 'info@azienda.it' },
              { name: 'vatNumber', label: 'P.IVA' },
              { name: 'taxId', label: 'Codice fiscale' },
              { name: 'country', label: 'Paese (ISO2)', defaultValue: 'IT' },
              { name: 'defaultCurrency', label: 'Valuta', kind: 'select', options: [
                { value: 'EUR', label: 'EUR' },
                { value: 'USD', label: 'USD' },
                { value: 'GBP', label: 'GBP' },
                { value: 'CHF', label: 'CHF' },
              ], defaultValue: 'EUR' },
              { name: 'plan', label: 'Piano', kind: 'select', options: [
                { value: 'free', label: 'Free' },
                { value: 'starter', label: 'Starter' },
                { value: 'basic', label: 'Basic' },
                { value: 'pro', label: 'Pro' },
                { value: 'enterprise', label: 'Enterprise' },
              ], defaultValue: 'free' },
              { name: 'enabled', label: 'Attivo', kind: 'boolean', defaultValue: true },
            ]}
          />
        }
      />
    </Stack>
  );
}
